import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, PlusIcon, EyeIcon, CalendarIcon, UsersIcon, ClockIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

interface Drive {
  id: string
  title: string
  description: string
  startDate: string
  endDate: string
  maxCandidates: number
  registeredCandidates: number
  testDuration: number
  status: 'upcoming' | 'active' | 'completed' | 'cancelled'
  createdAt: string
  collegeType: 'engineering' | 'management'
  tier: 'tier1' | 'tier2'
  role?: 'ASE' | 'Cloud Support Engineer' | 'Pre-Sales'
}

const DrivesListPage: React.FC = () => {
  const navigate = useNavigate()
  const [drives, setDrives] = useState<Drive[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'active' | 'completed'>('all')
  const [titleSearch, setTitleSearch] = useState('')
  const [startDateSearch, setStartDateSearch] = useState('')
  const datePickerRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadDrives()
  }, [])

  const updateDriveStatus = (drives: Drive[]) => {
    const now = new Date()
    return drives.map(drive => {
      const startDate = new Date(drive.startDate)
      const endDate = new Date(drive.endDate)
      
      let status = drive.status
      if (now < startDate) {
        status = 'upcoming'
      } else if (now >= startDate && now <= endDate) {
        status = 'active'
      } else if (now > endDate) {
        status = 'completed'
      }
      
      return { ...drive, status }
    })
  }

  const loadDrives = async () => {
    try {
      setIsLoading(true)
      console.log('Loading all drives from API...')
      
      // Fetch all drives using pagination to avoid missing any
      const pageSize = 500
      let allApiDrives: any[] = []
      let skip = 0
      let hasMore = true

      while (hasMore) {
        const response = await fetch(`/api/v1/drives/?limit=${pageSize}&skip=${skip}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const pageDrives = await response.json()
        allApiDrives = [...allApiDrives, ...pageDrives]
        
        // If we got fewer results than the page size, we've fetched everything
        if (pageDrives.length < pageSize) {
          hasMore = false
        } else {
          skip += pageSize
        }
      }

      console.log(`Total drives fetched: ${allApiDrives.length}`)
      
      // Transform API response to match frontend interface
      const transformedDrives: Drive[] = allApiDrives.map((drive: any) => ({
        id: drive.id.toString(),
        title: drive.title,
        description: drive.description || '',
        startDate: drive.start_date,
        endDate: drive.end_date,
        maxCandidates: drive.max_candidates,
        registeredCandidates: drive.registered_candidates,
        testDuration: drive.total_duration_minutes,
        status: drive.status as 'upcoming' | 'active' | 'completed' | 'cancelled',
        createdAt: drive.created_at,
        collegeType: drive.college_type as 'engineering' | 'management',
        tier: drive.tier as 'tier1' | 'tier2',
        role: drive.role as 'ASE' | 'Cloud Support Engineer' | 'Pre-Sales' | undefined
      }))
      
      // Update status based on current date
      const updatedDrives = updateDriveStatus(transformedDrives)
      
      setDrives(updatedDrives)
    } catch (error) {
      console.error('Error loading drives:', error)
      toast.error('Failed to load drives')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const filteredDrives = drives.filter(drive => {
    // Filter by status
    if (filter !== 'all' && drive.status !== filter) {
      return false
    }
    
    // Filter by title search
    if (titleSearch.trim() && !drive.title.toLowerCase().includes(titleSearch.toLowerCase().trim())) {
      return false
    }
    
    // Filter by start date search (text-based search in formatted date)
    if (startDateSearch.trim()) {
      const formattedStartDate = formatDate(drive.startDate)
      const searchText = startDateSearch.toLowerCase().trim()
      
      // Search in the formatted date string (e.g., "Nov 19, 2025, 12:47 PM")
      if (!formattedStartDate.toLowerCase().includes(searchText)) {
        return false
      }
    }
    
    return true
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800'
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'completed':
        return 'bg-gray-100 text-gray-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value
    if (selectedDate) {
      // Format the date to match the display format for better search results
      const date = new Date(selectedDate)
      const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
      setStartDateSearch(formattedDate)
    }
  }

  const handleCalendarIconClick = () => {
    // Trigger the date picker
    if (datePickerRef.current) {
      if (typeof datePickerRef.current.showPicker === 'function') {
        datePickerRef.current.showPicker()
      } else {
        datePickerRef.current.click()
      }
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading drives...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Recruitment Drives</h1>
              <p className="text-gray-600 mt-1">Manage and monitor your recruitment drives</p>
            </div>
            
            <button
              onClick={() => navigate('/recruiter/drives/new')}
              className="btn btn-primary flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create New Drive
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex space-x-4 mb-4">
            {[
              { key: 'all', label: 'All Drives' },
              { key: 'upcoming', label: 'Upcoming' },
              { key: 'active', label: 'Active' },
              { key: 'completed', label: 'Completed' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key as any)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          
          {/* Search Bars */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Drive Title Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by Drive Title..."
                value={titleSearch}
                onChange={(e) => setTitleSearch(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            
            {/* Start Date Search */}
            <div className="relative">
              <div 
                className="absolute inset-y-0 left-0 pl-3 flex items-center cursor-pointer hover:text-gray-600 transition-colors z-10"
                onClick={handleCalendarIconClick}
                title="Click to select date"
              >
                <CalendarIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </div>
              <input
                type="text"
                placeholder="Search by Start Date..."
                value={startDateSearch}
                onChange={(e) => setStartDateSearch(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {/* Hidden date picker */}
              <input
                ref={datePickerRef}
                type="date"
                onChange={handleDatePickerChange}
                className="sr-only"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        {/* Drives Grid */}
        {filteredDrives.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-24 w-24 text-gray-400 mb-4">
              <CalendarIcon className="w-full h-full" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No drives found</h3>
            <p className="text-gray-600 mb-6">
              {filter === 'all' 
                ? "You haven't created any drives yet." 
                : `No ${filter} drives found.`
              }
            </p>
            <button
              onClick={() => navigate('/recruiter/drives/new')}
              className="btn btn-primary flex items-center mx-auto"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Your First Drive
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDrives.map((drive) => (
              <div key={drive.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                    {drive.title}
                  </h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(drive.status)}`}>
                    {drive.status}
                  </span>
                </div>

                <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                  {drive.description}
                </p>
                
                <div className="flex items-center space-x-2 mb-4">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {drive.collegeType === 'engineering' ? 'Engineering' : 'Management'}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {drive.tier === 'tier1' ? 'Tier 1' : 'Tier 2'}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    <span>Start: {formatDate(drive.startDate)}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    <span>End: {formatDate(drive.endDate)}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <UsersIcon className="w-4 h-4 mr-2" />
                    <span>{drive.registeredCandidates} / {drive.maxCandidates} candidates</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="w-4 h-4 mr-2" />
                    <span>{drive.testDuration} minutes</span>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => navigate(`/recruiter/drives/${drive.id}`)}
                    className="btn btn-outline flex items-center text-sm"
                  >
                    <EyeIcon className="w-4 h-4 mr-2" />
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default DrivesListPage
